import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Building2,
  ShieldCheck,
  Clock3,
  Send,
  CheckCircle2,
  Lock,
  Loader2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ORG_TYPES = [
  'Hospital / Health System',
  'Skilled Nursing / Long-Term Care',
  'Rehabilitation / Therapy Facility',
  'Dialysis Center',
  'Insurance / TPA',
  'NEMT Broker / Transportation Coordinator',
  "Workers' Compensation Payer",
  'Case Management / Care Coordination',
  'Home Health / Hospice',
  'Physician Group / Clinic',
  'Other',
];

const VOLUMES = [
  '1–10 trips / month',
  '11–50 trips / month',
  '51–150 trips / month',
  '151–500 trips / month',
  '500+ trips / month',
  'Not sure yet',
];

const INTERESTS = [
  'Recurring dialysis transportation',
  'Hospital discharge & follow-up',
  'Rehabilitation / therapy transportation',
  "Workers' Compensation transportation",
  'Insurance / TPA network onboarding',
  'NEMT broker capacity partnership',
  'Facility contract (multi-service)',
  'Procurement / credentialing review',
];

const TRANSPORT_TYPES = ['Wheelchair', 'Ambulatory', 'Recurring / Standing schedule'];

const DEFAULTS = {
  organization: '',
  org_type: '',
  contact_name: '',
  title: '',
  email: '',
  phone: '',
  department: '',
  interest: '',
  volume: '',
  transport_type: [],
  message: '',
  consent: false,
};

const PartnerLeadForm = ({
  eyebrow = 'PROVIDER RELATIONS',
  heading = 'Request a Partnership Conversation',
  subheading = 'Tell us about your patients and volume. A member of our Provider Relations team will reply within one business day with next steps.',
  defaultInterest,
  defaultOrgType,
  sourcePage,
}) => {
  const [form, setForm] = useState({
    ...DEFAULTS,
    interest: defaultInterest || '',
    org_type: defaultOrgType || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleTransport = (t) =>
    setForm((f) => ({
      ...f,
      transport_type: f.transport_type.includes(t)
        ? f.transport_type.filter((x) => x !== t)
        : [...f.transport_type, t],
    }));

  const validate = () => {
    if (!form.organization.trim()) return 'Organization is required.';
    if (!form.org_type) return 'Please select an organization type.';
    if (!form.contact_name.trim()) return 'Contact name is required.';
    if (!form.email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
      return 'A valid work email is required.';
    if (!form.consent) return 'Please confirm you are authorized to contact MoveraCare on behalf of your organization.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        organization: form.organization.trim(),
        org_type: form.org_type,
        contact_name: form.contact_name.trim(),
        title: form.title.trim() || undefined,
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        department: form.department.trim() || undefined,
        interest: form.interest || undefined,
        volume: form.volume || undefined,
        transport_type:
          form.transport_type.length > 0 ? form.transport_type.join(', ') : undefined,
        message: form.message.trim() || undefined,
        source_page: sourcePage,
      };
      const { data } = await axios.post(`${API}/api/inquiries/vendor`, payload);
      setSubmitted({
        id: data.id,
        ref: data.ref || data.id.slice(0, 8).toUpperCase(),
        email_status: data.email_status,
        overall: data.status,
        ...payload,
      });
      if (data.status === 'received' && data.email_status === 'sent') {
        toast.success('Thanks — Provider Relations has been notified and will reach out within one business day.');
      } else if (data.status === 'received_email_failed') {
        toast.warning('Your inquiry was saved, but our automated email could not be delivered. A team member will still follow up — you may also email dispatch@moveracaremobility.com directly.');
      } else {
        toast.success('Thanks — your inquiry has been received.');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Something went wrong. Please try again or email dispatch@moveracaremobility.com.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    const emailFailed = submitted.overall === 'received_email_failed';
    return (
      <div
        className={`relative overflow-hidden rounded-2xl border-2 p-8 md:p-12 shadow-xl ${
          emailFailed
            ? 'border-orange-400 bg-gradient-to-br from-white to-orange-50'
            : 'border-[#F9A825] bg-gradient-to-br from-white to-[#FFF8E1]'
        }`}
        data-testid="partner-lead-form-success"
      >
        <div className="flex items-start gap-5">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${
            emailFailed ? 'bg-orange-500' : 'bg-[#F9A825]'
          }`}>
            <CheckCircle2 className="w-8 h-8 text-white" />
          </div>
          <div className="flex-1">
            <p className={`text-xs font-bold tracking-wider mb-2 ${emailFailed ? 'text-orange-600' : 'text-[#F9A825]'}`}>
              REQUEST RECEIVED · REF #{submitted.ref}
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-[#004C97] mb-3">
              {emailFailed
                ? `Saved, ${submitted.contact_name.split(' ')[0]} — notification delivery pending.`
                : `Thank you, ${submitted.contact_name.split(' ')[0]} — we'll be in touch.`}
            </h3>
            {emailFailed ? (
              <>
                <p className="text-gray-800 mb-4 leading-relaxed">
                  Your inquiry is saved with reference <strong>#{submitted.ref}</strong>, but our
                  automated email to Provider Relations couldn&apos;t be delivered. Our team monitors
                  this queue and will still follow up.
                </p>
                <p className="text-sm text-gray-700 mb-6">
                  If you don&apos;t hear from us within one business day, email{' '}
                  <a href="mailto:dispatch@moveracaremobility.com" className="text-[#004C97] font-semibold underline">
                    dispatch@moveracaremobility.com
                  </a>{' '}
                  or call <a href="tel:+18329252128" className="text-[#004C97] font-semibold underline">832-925-2128</a> and reference #{submitted.ref}.
                </p>
              </>
            ) : (
              <>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Your partnership inquiry has been routed to Provider Relations. A member of our team
                  will reply to <strong>{submitted.email}</strong> within one business day with:
                </p>
                <ul className="space-y-2 text-sm text-gray-700 mb-6">
                  {[
                    'A short discovery call to understand your volume and workflow',
                    'Vendor procurement packet, W-9, COI, and credentialing documentation',
                    'Recommended service tier and turnaround commitments for your account',
                  ].map((line) => (
                    <li key={line} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#F9A825] mt-0.5 flex-shrink-0" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() =>
                  setSubmitted(null) ||
                  setForm({
                    ...DEFAULTS,
                    interest: defaultInterest || '',
                    org_type: defaultOrgType || '',
                  })
                }
                variant="outline"
                className="border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white"
                data-testid="partner-lead-form-submit-another"
              >
                Submit Another Inquiry
              </Button>
              <a href="/MoveraCare-Vendor-Procurement-Packet.pdf" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold">
                  Download Procurement Packet
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative overflow-hidden rounded-2xl border-2 border-[#004C97]/10 bg-white shadow-xl"
      data-testid="partner-lead-form"
    >
      {/* Accent bar */}
      <div className="h-1.5 w-full bg-gradient-to-r from-[#004C97] via-[#F9A825] to-[#004C97]" />
      <div className="grid grid-cols-1 lg:grid-cols-5">
        {/* Sidebar */}
        <aside className="lg:col-span-2 bg-[#004C97] text-white p-8 md:p-10">
          <div className="inline-flex items-center gap-2 bg-[#F9A825] text-[#004C97] px-3 py-1 rounded-full text-xs font-bold mb-4">
            <Building2 className="w-3.5 h-3.5" />
            <span>{eyebrow}</span>
          </div>
          <h3 className="text-3xl md:text-4xl font-bold leading-tight mb-4">{heading}</h3>
          <p className="text-gray-200 text-sm leading-relaxed mb-8">{subheading}</p>

          <div className="space-y-5 border-t border-white/10 pt-6">
            {[
              {
                icon: Clock3,
                title: 'One Business Day Response',
                body: 'Provider Relations replies to every qualified partner inquiry within 24 business hours.',
              },
              {
                icon: ShieldCheck,
                title: 'Credentialing-Ready',
                body: 'W-9, COI, vendor packet, and HIPAA business associate materials available on request.',
              },
              {
                icon: Lock,
                title: 'HIPAA-Aware Intake',
                body: 'Please do not include Protected Health Information. Patient trip details are captured separately after contracting.',
              },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-[#F9A825]" />
                </div>
                <div>
                  <p className="font-semibold text-sm mb-0.5">{title}</p>
                  <p className="text-xs text-gray-300 leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="lg:col-span-3 p-8 md:p-10 space-y-5"
          data-testid="partner-lead-form-fields"
          noValidate
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <Label htmlFor="plf-organization" className="text-[#004C97] font-semibold">
                Organization <span className="text-red-500">*</span>
              </Label>
              <Input
                id="plf-organization"
                value={form.organization}
                onChange={(e) => set('organization', e.target.value)}
                placeholder="e.g., Fort Bend Regional Medical Center"
                required
                className="mt-1.5"
                data-testid="plf-organization"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="plf-org-type" className="text-[#004C97] font-semibold">
                Organization Type <span className="text-red-500">*</span>
              </Label>
              <Select value={form.org_type} onValueChange={(v) => set('org_type', v)}>
                <SelectTrigger id="plf-org-type" className="mt-1.5" data-testid="plf-org-type">
                  <SelectValue placeholder="Select your organization type" />
                </SelectTrigger>
                <SelectContent>
                  {ORG_TYPES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`plf-org-type-opt-${t.slice(0, 12).replace(/\W+/g, '-').toLowerCase()}`}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="plf-contact-name" className="text-[#004C97] font-semibold">
                Contact Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="plf-contact-name"
                value={form.contact_name}
                onChange={(e) => set('contact_name', e.target.value)}
                placeholder="Full name"
                required
                className="mt-1.5"
                data-testid="plf-contact-name"
              />
            </div>

            <div>
              <Label htmlFor="plf-title" className="text-[#004C97] font-semibold">
                Title / Role
              </Label>
              <Input
                id="plf-title"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="e.g., Case Manager, Director of Procurement"
                className="mt-1.5"
                data-testid="plf-title"
              />
            </div>

            <div>
              <Label htmlFor="plf-email" className="text-[#004C97] font-semibold">
                Work Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="plf-email"
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                placeholder="name@yourorganization.com"
                required
                className="mt-1.5"
                data-testid="plf-email"
              />
            </div>

            <div>
              <Label htmlFor="plf-phone" className="text-[#004C97] font-semibold">
                Direct Phone
              </Label>
              <Input
                id="plf-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                placeholder="(555) 123-4567"
                className="mt-1.5"
                data-testid="plf-phone"
              />
            </div>

            <div>
              <Label htmlFor="plf-department" className="text-[#004C97] font-semibold">
                Department
              </Label>
              <Input
                id="plf-department"
                value={form.department}
                onChange={(e) => set('department', e.target.value)}
                placeholder="e.g., Case Management, Procurement"
                className="mt-1.5"
                data-testid="plf-department"
              />
            </div>

            <div>
              <Label htmlFor="plf-volume" className="text-[#004C97] font-semibold">
                Estimated Monthly Volume
              </Label>
              <Select value={form.volume} onValueChange={(v) => set('volume', v)}>
                <SelectTrigger id="plf-volume" className="mt-1.5" data-testid="plf-volume">
                  <SelectValue placeholder="Select an estimate" />
                </SelectTrigger>
                <SelectContent>
                  {VOLUMES.map((v) => (
                    <SelectItem key={v} value={v}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="plf-interest" className="text-[#004C97] font-semibold">
                Primary Interest
              </Label>
              <Select value={form.interest} onValueChange={(v) => set('interest', v)}>
                <SelectTrigger id="plf-interest" className="mt-1.5" data-testid="plf-interest">
                  <SelectValue placeholder="What kind of partnership are you exploring?" />
                </SelectTrigger>
                <SelectContent>
                  {INTERESTS.map((i) => (
                    <SelectItem key={i} value={i}>
                      {i}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label className="text-[#004C97] font-semibold">Transportation Modes Needed</Label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
                {TRANSPORT_TYPES.map((t) => {
                  const active = form.transport_type.includes(t);
                  return (
                    <button
                      type="button"
                      key={t}
                      onClick={() => toggleTransport(t)}
                      className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2.5 text-sm font-semibold text-left transition-colors ${
                        active
                          ? 'border-[#F9A825] bg-[#FFF8E1] text-[#004C97]'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-[#F9A825]/60'
                      }`}
                      data-testid={`plf-transport-${t.split(' ')[0].toLowerCase()}`}
                    >
                      <span
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                          active ? 'border-[#F9A825] bg-[#F9A825]' : 'border-gray-300 bg-white'
                        }`}
                      >
                        {active && <CheckCircle2 className="w-3 h-3 text-white" />}
                      </span>
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="plf-message" className="text-[#004C97] font-semibold">
                Anything else we should know?
              </Label>
              <Textarea
                id="plf-message"
                value={form.message}
                onChange={(e) => set('message', e.target.value)}
                placeholder="Volume patterns, service area, contracting timeline, current pain points, etc. Please do not include patient PHI."
                rows={4}
                className="mt-1.5"
                data-testid="plf-message"
              />
            </div>

            <div className="md:col-span-2 flex items-start gap-3 pt-2">
              <Checkbox
                id="plf-consent"
                checked={form.consent}
                onCheckedChange={(v) => set('consent', v === true)}
                className="mt-0.5"
                data-testid="plf-consent"
              />
              <Label htmlFor="plf-consent" className="text-sm text-gray-600 leading-relaxed">
                I&apos;m authorized to contact MoveraCare Mobility on behalf of my organization and
                consent to being contacted by Provider Relations regarding this inquiry.
              </Label>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
            <p className="text-xs text-gray-500 leading-relaxed">
              We reply within one business day. For same-day patient transportation, use the{' '}
              <a
                href="/request-patient-transportation"
                className="text-[#004C97] font-semibold hover:text-[#F9A825] underline"
              >
                Patient Transportation Request form
              </a>
              .
            </p>
            <Button
              type="submit"
              size="lg"
              disabled={submitting}
              className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold px-8 shadow-lg w-full sm:w-auto"
              data-testid="plf-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Partnership Inquiry
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PartnerLeadForm;
